<script setup lang="ts">
  import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import { Button } from '@/components/ui/button'
  import { User, Building2, Landmark } from 'lucide-vue-next'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import { type Option } from '@/components/admin/form/SelectField.vue'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { useTranslation } from '@/composables/useTranslation'
  import {
    RelationshipTypeEnum,
    EntityTypeEnum,
    type CustomerEntityWithRelationship as EntityWithRelationship
  } from '@/types/generated'
  import { EntitiesService } from '@/types/generated'

  const { t } = useTranslation({
    en: {
      searchEntities: 'Search entities...',
      noEntitiesFound: 'No entities found.',
      noEntitiesAvailable: 'No entities available for replacement.',
      cancel: 'Cancel',
      addTitle: 'Add New Client/Opponent',
      removeTitle: 'Replace Client/Opponent',
      addDescription: 'Select an entity to replace as the case client/opponent.',
      removeDescription: 'Select a replacement entity for the current client/opponent.',
      noName: 'Unnamed'
    },
    de: {
      searchEntities: 'Entitäten suchen...',
      noEntitiesFound: 'Keine Entitäten gefunden.',
      noEntitiesAvailable: 'Keine Entitäten für den Ersatz verfügbar.',
      cancel: 'Abbrechen',
      addTitle: 'Neuen Mandanten/Gegner hinzufügen',
      removeTitle: 'Mandanten/Gegner ersetzen',
      addDescription: 'Wählen Sie eine Entität aus, die als Mandant/Gegner ersetzt werden soll.',
      removeDescription: 'Wählen Sie eine Ersatzentität für den aktuellen Mandanten/Gegner aus.',
      noName: 'Unbenannt'
    }
  })

  const props = defineProps({
    isOpen: {
      type: Boolean,
      required: true
    },
    action: {
      type: String,
      required: true,
      validator: (value: string) => ['add', 'remove'].includes(value)
    },
    relationshipType: {
      type: String,
      required: true,
      validator: (value: string) => {
        const allowedTypes = [
          RelationshipTypeEnum.ENTITIES_CLIENT,
          RelationshipTypeEnum.ENTITIES_OPPONENT
        ]
        return allowedTypes.includes(value as any)
      }
    },
    relationshipUuid: {
      type: String,
      required: false,
      default: ''
    }
  })

  const emit = defineEmits(['update:isOpen', 'entitySelected', 'relationshipUpdated'])

  const caseStore = useCaseStore()
  const caseRecord = computed(() => caseStore.caseRecord)

  const newEntityRelationshipWhichShouldHoldTheDesignatedRelationship =
    ref<EntityWithRelationship | null>(null)
  const selectedEntityRelationshipFromTheModalDropdown = ref<EntityWithRelationship | null>(null)

  // Filter out the current entity and entities that already have the client/opponent relationship
  const entities = computed<EntityWithRelationship[]>(() => {
    if (!caseRecord.value?.data?.entities) return []

    return caseRecord.value.data.entities.filter((entity) => {
      // Filter out the current entity
      if (entity.entity.uuid === props.relationshipUuid) return false

      // Filter out entities that already have this relationship type
      const hasRelationship = entity.relationships.some(
        (r) => r.relationship_type === props.relationshipType
      )

      const isOpponent = entity.relationships.some(
        (r) => r.relationship_type === RelationshipTypeEnum.ENTITIES_OPPONENT
      )

      const isClient = entity.relationships.some(
        (r) => r.relationship_type === RelationshipTypeEnum.ENTITIES_CLIENT
      )
      return !hasRelationship && !isOpponent && !isClient
    })
  })

  const selectedEntityRelationship = computed<EntityWithRelationship>(() => {
    const relationship = caseRecord.value?.data.entities.find((entity) => {
      return entity.uuid === props.relationshipUuid
    })

    if (!caseRecord.value?.data?.entities || !relationship) {
      close()
    }
    return relationship!
  })

  const entityRelationshipCurrentlyHoldingTheDesignatedRelationship =
    computed<EntityWithRelationship>(() => {
      const relationship = caseRecord.value?.data.entities.find((entity) => {
        return entity.relationships.some((r) => r.relationship_type === props.relationshipType)
      })
      if (!caseRecord.value?.data?.entities || !relationship) {
        close()
      }
      return relationship!
    })

  const isSelectedRelationShipClient = computed(() => {
    return selectedEntityRelationship.value?.relationships.find(
      (r) => r.relationship_type === RelationshipTypeEnum.ENTITIES_CLIENT
    )
  })

  const isSelectedRelationshipOpponent = computed(() => {
    return selectedEntityRelationship.value?.relationships.find(
      (r) => r.relationship_type === RelationshipTypeEnum.ENTITIES_OPPONENT
    )
  })

  // Define functions early to avoid reference errors
  const updateOpen = (value: boolean) => {
    newEntityRelationshipWhichShouldHoldTheDesignatedRelationship.value = null
    selectedEntityRelationshipFromTheModalDropdown.value = null
    emit('update:isOpen', value)
  }

  const close = () => {
    newEntityRelationshipWhichShouldHoldTheDesignatedRelationship.value = null
    selectedEntityRelationshipFromTheModalDropdown.value = null
    emit('update:isOpen', false)
  }

  // Watch for changes in selectedEntityRelationship and props.action
  watch(
    [selectedEntityRelationship, () => props.action],
    ([newSelectedEntityRelationship, newAction]) => {
      newEntityRelationshipWhichShouldHoldTheDesignatedRelationship.value = null
      if (newAction === 'add') {
        if (!isSelectedRelationshipOpponent.value && !isSelectedRelationShipClient.value) {
          newEntityRelationshipWhichShouldHoldTheDesignatedRelationship.value =
            newSelectedEntityRelationship
        }
      }
    },
    { immediate: true }
  )

  const selectEntity = async (entity?: EntityWithRelationship) => {
    if (!entity) return
    if (
      props.action === 'add' &&
      (isSelectedRelationshipOpponent.value || isSelectedRelationShipClient.value)
    ) {
      // The entity the user currently wants to change its relationship
      newEntityRelationshipWhichShouldHoldTheDesignatedRelationship.value =
        selectedEntityRelationship.value
      // The entity the user selected in the dropdown
      selectedEntityRelationshipFromTheModalDropdown.value = entity
      return
    }
    newEntityRelationshipWhichShouldHoldTheDesignatedRelationship.value = entity
  }

  const formatEntityName = (entity: EntityWithRelationship) => {
    if (entity.entity.entity_type === EntityTypeEnum.NATURAL_PERSON) {
      if (!entity.entity.title && !entity.entity.first_name && !entity.entity.last_name)
        return t('noName')
      return `${entity.entity.title} ${entity.entity.first_name} ${entity.entity.last_name}`
    } else if (entity.entity.entity_type === EntityTypeEnum.LEGAL_PERSON) {
      if (!entity.entity.company_name) return t('noName')
      return entity.entity.company_name
    } else if (entity.entity.entity_type === EntityTypeEnum.INSTITUTION) {
      if (!entity.entity.institution_name) return t('noName')
      return entity.entity.institution_name
    }
  }

  const selectableNonConflictingEntityRelationshipOptions = computed<Option[]>(() => {
    return entities.value.map((entity) => ({
      value: entity.uuid,
      title: formatEntityName(entity),
      icon:
        entity.entity.entity_type === EntityTypeEnum.NATURAL_PERSON
          ? User
          : entity.entity.entity_type === EntityTypeEnum.LEGAL_PERSON
            ? Building2
            : Landmark
    })) as Option[]
  })

  const handleConfirmRelationshipReplacement = async () => {
    if (
      !newEntityRelationshipWhichShouldHoldTheDesignatedRelationship.value &&
      !selectedEntityRelationshipFromTheModalDropdown.value
    )
      return
    try {
      const selectedRelationshipType = isSelectedRelationShipClient.value
        ? RelationshipTypeEnum.ENTITIES_CLIENT
        : RelationshipTypeEnum.ENTITIES_OPPONENT
      const response = await EntitiesService.entitiesSwitchUniqueCaseDataRelationshipCreate({
        requestBody: {
          new_entity_relationship_which_should_hold_the_designated_relationship_uuid:
            newEntityRelationshipWhichShouldHoldTheDesignatedRelationship.value!.uuid!,
          case_data_uuid: caseRecord.value!.data.uuid!,
          relationship_type: props.relationshipType as RelationshipTypeEnum
        }
      })

      // Update local state
      newEntityRelationshipWhichShouldHoldTheDesignatedRelationship.value!.relationships =
        response.new_entity_relationship_which_should_hold_the_designated_relationship.relationships
      entityRelationshipCurrentlyHoldingTheDesignatedRelationship.value!.relationships =
        response.entity_relationship_currently_holding_the_designated_relationship.relationships

      if (selectedEntityRelationshipFromTheModalDropdown.value) {
        await EntitiesService.entitiesSwitchUniqueCaseDataRelationshipCreate({
          requestBody: {
            new_entity_relationship_which_should_hold_the_designated_relationship_uuid:
              selectedEntityRelationshipFromTheModalDropdown.value!.uuid!,
            case_data_uuid: caseRecord.value!.data.uuid!,
            relationship_type: selectedRelationshipType
          }
        })
      }

      // Emit event to parent to refresh case data
      emit('relationshipUpdated', response)
      close()
    } catch (error) {
      console.error('Error updating relationship:', error)
    }
  }
</script>

<template>
  <Dialog
    :open="isOpen"
    @update:open="updateOpen"
  >
    <DialogContent class="sm:max-w-[500px]">
      <DialogDescription></DialogDescription>
      <DialogHeader>
        <DialogTitle>
          <template v-if="props.action === 'add'">
            {{
              props.relationshipType === RelationshipTypeEnum.ENTITIES_CLIENT
                ? 'Neuen Mandant festlegen'
                : 'Neuen Gegner festlegen'
            }}
          </template>
          <template v-else>
            {{
              props.relationshipType === RelationshipTypeEnum.ENTITIES_CLIENT
                ? 'Mandant entfernen'
                : 'Gegner entfernen'
            }}
          </template>
        </DialogTitle>
      </DialogHeader>
      <div class="py-4">
        <template v-if="props.action === 'add'">
          <!-- ADDING -->
          <template v-if="isSelectedRelationshipOpponent || isSelectedRelationShipClient">
            <!-- CONFLICT: Trying to switch Opponent <-> Client, or vice versa. -->
            <!-- Debug info removed -->
            <div class="mb-4">
              <p class="my-3">
                Wenn Sie
                <strong>
                  {{
                    selectedEntityRelationship ? formatEntityName(selectedEntityRelationship) : ''
                  }}
                </strong>
                als
                <strong>
                  {{
                    props.relationshipType === RelationshipTypeEnum.ENTITIES_CLIENT
                      ? 'Mandant'
                      : 'Gegner'
                  }}
                </strong>
                festlegen, benötigen Sie einen neuen
                {{
                  props.relationshipType === RelationshipTypeEnum.ENTITIES_CLIENT
                    ? 'Gegner'
                    : 'Mandant'
                }}.
              </p>

              <div class="mt-3 flex items-center">
                <span class="mr-2">
                  Bitte wählen Sie einen neuen
                  <strong>
                    {{
                      props.relationshipType === RelationshipTypeEnum.ENTITIES_CLIENT
                        ? 'Gegner'
                        : 'Mandant'
                    }}
                  </strong>
                  :
                </span>
              </div>
            </div>
            <SelectField
              v-if="entities.length > 0"
              class="mt-2"
              name="entity-selection"
              :placeholder="t('searchEntities')"
              :options="selectableNonConflictingEntityRelationshipOptions"
              :model-value="null"
              @update:model-value="
                (value) => selectEntity(entities.find((entity) => entity.uuid === value))
              "
            />
          </template>
          <template v-else>
            <!-- No conflict: Just Need to confirm that they actually want that -->
            Sie sind dabei den
            {{
              props.relationshipType === RelationshipTypeEnum.ENTITIES_CLIENT
                ? 'Mandanten'
                : 'Gegner'
            }}
            von
            <strong>
              {{ formatEntityName(entityRelationshipCurrentlyHoldingTheDesignatedRelationship) }}
            </strong>
            zu
            <strong>
              {{ formatEntityName(selectedEntityRelationship) }}
            </strong>
            zu wechseln.
            <br />
          </template>
        </template>
        <!-- REMOVING -->
        <template v-else>
          <!-- CONFLICT: Trying to remove Opponent or Client -->
          Sie müssen einen anderen
          <strong>
            {{
              props.relationshipType === RelationshipTypeEnum.ENTITIES_CLIENT
                ? 'Mandanten'
                : 'Gegner'
            }}
          </strong>
          auswählen
          <br />
          <SelectField
            v-if="entities.length > 0"
            class="mt-2"
            name="entity-selection"
            :placeholder="t('searchEntities')"
            :options="selectableNonConflictingEntityRelationshipOptions"
            :model-value="null"
            @update:model-value="
              (value) => selectEntity(entities.find((entity) => entity.uuid === value))
            "
          />
        </template>
      </div>
      <div class="border-t border-gray-200 pt-4">
        <div class="flex items-center justify-between">
          <Button
            variant="outline"
            @click="close"
          >
            {{ t('cancel') }}
          </Button>
          <button
            class="inline-flex items-center gap-x-2 rounded-lg border border-transparent bg-purple-600 px-3 py-2
              text-sm font-semibold text-white hover:bg-purple-700 focus:outline-none focus:ring-2
              focus:ring-blue-500 disabled:pointer-events-none disabled:opacity-50"
            :disabled="
              !selectedEntityRelationshipFromTheModalDropdown &&
              !newEntityRelationshipWhichShouldHoldTheDesignatedRelationship
            "
            @click="handleConfirmRelationshipReplacement"
          >
            Bestätigen
          </button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
